import { dateFormatters, formatters, ValueInput } from '@utils/formattersV2';
import { calculateDataCountByDayOfWeek, calculateLatestDate } from './dates';

export type OptimizationChartOptionsProps = {
  yAxisLabel: string;
  yAxisFormatter?: (value: ValueInput) => string;
  valueFormatter?: (value: ValueInput) => string;
  dates: string[];
  series: {
    data: {
      date: string;
      value: number;
    }[];
    name: string;
    visible: boolean;
    color: string;
  }[];
};

const SYMBOL_RADIUS = 16;

export const optimizationChartOptions = (
  props: OptimizationChartOptionsProps
): Highcharts.Options => {
  const dataCountByDayOfWeek = calculateDataCountByDayOfWeek(props.dates);
  const latestDate = calculateLatestDate(props.dates);

  return {
    credits: {
      enabled: false,
    },

    exporting: {
      enabled: false,
    },

    legend: {
      enabled: false,
    },

    title: {
      text: undefined,
    },

    xAxis: {
      type: 'datetime',
      labels: {
        format: '{value:%m/%d/%Y}',
      },
      startOfWeek: Object.entries(dataCountByDayOfWeek).reduce(
        (acc, [dayOfWeek, dataCount]) => {
          if (dataCount > (dataCountByDayOfWeek[acc] ?? 0)) {
            return Number(dayOfWeek);
          }

          return acc;
        },
        0
      ),
      max: latestDate,
    },

    yAxis: {
      min: 0,
      title: {
        text: props.yAxisLabel,
      },
      labels: {
        formatter() {
          const numberValue = Number(this.value);

          return (
            props.yAxisFormatter?.(numberValue) ??
            formatters.percentage(numberValue)
          );
        },
      },
    },

    tooltip: {
      shared: true,
      formatter() {
        return this.points
          ? `<b>${dateFormatters.shortLocaleString(
              new Date(this.x ?? '')
            )}</b><br />${this.points
              .sort((a, b) => (b.y ?? 0) - (a.y ?? 0))
              .map(
                point =>
                  // TODO: Fix type cast
                  `<span style="color: ${point.color as string}">\u25CF</span> ${
                    point.series.name
                  }: ${props.valueFormatter?.(point.y) ?? formatters.lift(point.y)}`
              )
              .join('<br />')}`
          : undefined;
      },
    },

    series: props.series.map(series => ({
      type: 'spline',
      name: series.name,
      color: series.color,
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: SYMBOL_RADIUS / 2,
      },
      visible: series.visible,
      data: series.data
        .map(dataPoint => ({
          x: Date.parse(dataPoint.date),
          y: dataPoint.value,
        }))
        .sort((a, b) => a.x - b.x),
    })),
  };
};
